/* eslint-disable react-hooks/exhaustive-deps */
import useSWR from 'swr';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';

import useAuthToken from './useAuthToken';
import redirectToStripe from '../utils/stripe';
import LoadingIcon from '../icons/LoadingIcon';
import { handleSignOut } from '../handlers/signOut';
import { Subscription } from '../types/Subscription';
import userClient from '../utils/apiClients/userClient';
import { ProfileState } from '../store/slices/profileSlice';
import { useAppSelector } from '../store/store';
import { getActiveProfile, setActiveProfile } from '../components/context/AppContext';

export function StoreError(
  message: string,
  ...extraErrors: (Error | unknown)[]
) {
  try {
    const existingLogs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
    const errorEntry = {
      timestamp: new Date().toISOString(),
      message,
      extraErrors
    };
    existingLogs.push(errorEntry);
    localStorage.setItem('errorLogs', JSON.stringify(existingLogs));
  } catch (storageError) {
    console.error('Failed to store error log:', storageError);
  }
}

export const stripRedirect = (subscription?: Subscription | null) => {
  const planId = localStorage.getItem('planId');

  if (planId && planId == 'price_free') {
    return;
  }

  if (planId) {
    if (subscription) {
      redirectToStripe(planId, !subscription?.free && subscription?.active);
      return;
    } else {
      redirectToStripe(planId, false);
      return;
    }
  }
};

export const stripRedirectSubscription = (subscription: Subscription | null) => {
  const planId = localStorage.getItem('planId');

  if (!planId) return;

  if (planId && planId == 'price_free') {
    return;
  }

  // Use subscription from Redux store instead of making another API call
  const isSubscribed = Boolean(subscription?.active && !subscription?.free);
  redirectToStripe(planId, isSubscribed);
};


const fetchUser = async () => {
  const authUser = await getCurrentUser();
  const { data } = await userClient.post('/login', {});
  if (authUser && data) return true;
  return false;
};

const useNavigation = () => {

  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileState>(null);
  const [loading, setIsLoading] = useState<boolean>(false);
  const { onCanvas } = useAppSelector(e => e.globalState);
  const { subscription, loading: subscriptionLoading } = useAppSelector(e => e.subscription);

  const { isAuthenticated, isLoading: authLoading, error: authError } = useAuthToken();
  const { data: user, isLoading: userLoading, error: userError } = useSWR(isAuthenticated ? 'user' : null, fetchUser);
  const isLoading = authLoading || userLoading;

  const initialize = async (): Promise<void> => {
    try {
      setIsLoading(true);
      if (!isAuthenticated) return;
      const { data } = await userClient.get('users/me/profiles');
      setProfile({ profile: data, isLoading: false });
    } catch (error: unknown) {
      StoreError('An error was generated while checking the current user', error);
      handleSignOut();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userError || authError) {
      console.error('An error occurred while calling these api endpoints  getCurrentUser , /login  , useAuthToken , users/me/profiles', authError, userError);
      StoreError('An error occurred while calling these api endpoints  getCurrentUser , /login  , useAuthToken , users/me/profiles', userError, authError);
      handleSignOut();
    }
  }, [userError]);

  // Subscription is now loaded in AppContext, no need to fetch here

  useEffect(() => {
    if (profile === null && !loading && !isLoading) initialize();
  }, [user, profile, loading, isAuthenticated, isLoading]);

  useEffect(() => {
    if (loading || user === null) return;

    const redirectToAppropriatePage = () => {

      if (!user) {
        navigate('/');
        return;
      }

      if (profile) {

        const { artist, creator, influencer } = profile?.profile || {};

        if (!artist && !creator && !influencer) {
          navigate('/signup/select-profile');
          return;
        }

        if (getActiveProfile()) {
          navigate(getActiveProfile() + '/dashboard');
          return;
        }

        if (creator) {
          setActiveProfile('creator');
          stripRedirect(subscription);
          navigate('/creator/dashboard');
          return;
        }

        if (influencer) {
          setActiveProfile('influencer');
          stripRedirect(subscription);
          navigate('/influencer/dashboard');
          return;
        }

        if (artist) {
          setActiveProfile('artist');
          stripRedirect(subscription);
          navigate('/artist/dashboard');
          return;
        }


      }
    };

    redirectToAppropriatePage();
  }, [loading, user, profile, onCanvas]);

  if (loading || isLoading || subscriptionLoading) {
    return (
      <div className="grid place-items-center h-screen w-full">
        <div>
          <LoadingIcon width={150} height={150} />
          <p className="font-inter text-semibold mt-4">Validating Access...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default useNavigation;
